import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Eye, EyeOff } from "lucide-react";

import { cn } from "../../../../lib/utils";

import { Helper } from "./helper";

const inputVariants = cva(
  [
    "flex w-full bg-transparent [outline:none]",
    "focus:outline-none focus:ring-0 focus:ring-offset-0",
    "focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
    "file:border-0 file:hidden file:bg-transparent file:font-medium",
    "disabled:cursor-not-allowed disabled:text-semantic-disabled-foreground-global-default disabled:border-semantic-disabled-stroke-global-default",
    "placeholder:transition-opacity placeholder:duration-200",
    "focus:placeholder:opacity-0",
    "rounded-sm border-semantic-secondary-stroke-normal-default hover:border-semantic-secondary-stroke-normal-hovered focus:border-semantic-secondary-stroke-normal-pressed",
    "typography-semantic-label-1-normal---regular",
  ],
  {
    variants: {
      size: {
        md: "h-8 px-sp160 py-sp60",
        lg: "h-9 px-sp160 py-sp120",
        xlg: "h-10 px-sp200 py-sp160",
      },
      style: {
        filled: [
          "text-semantic-secondary-foreground-normal",
          "placeholder:text-semantic-secondary-foreground-inverse-default",
        ],
        outlined: [
          "transition duration-100",
          "text-semantic-secondary-foreground-normal",
          "placeholder:text-semantic-secondary-foreground-inverse-default",
        ],
        transparent: [
          "border-0",
          "text-semantic-secondary-foreground-normal",
          "placeholder:text-semantic-secondary-foreground-inverse-default",
        ],
      },
    },
    defaultVariants: {
      size: "md",
      style: "filled",
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "style">,
    VariantProps<typeof inputVariants> {
  error?: string;
  showPlaceholder?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  showPasswordToggle?: boolean;
  plainReadOnly?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      style,
      size,
      type,
      showPlaceholder = false,
      error,
      leftIcon,
      rightIcon,
      showPasswordToggle = false,
      readOnly,
      plainReadOnly = false,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);

    const inputType = React.useMemo(() => {
      if (type === "password" && showPassword) {
        return "text";
      }
      return type;
    }, [type, showPassword]);

    const PasswordToggleIcon = React.useMemo(() => {
      if (type !== "password" || !showPasswordToggle) return null;

      return (
        <button
          type="button"
          onClick={() => setShowPassword((prev) => !prev)}
          className="flex items-center justify-center focus:outline-none"
          tabIndex={-1}
        >
          {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
      );
    }, [type, showPasswordToggle, showPassword]);

    const finalRightIcon = React.useMemo(() => {
      if (PasswordToggleIcon) {
        return PasswordToggleIcon;
      }
      return rightIcon;
    }, [PasswordToggleIcon, rightIcon]);

    return (
      <div className="flex flex-col gap-sp40 w-full">
        <div className="relative flex items-center">
          {leftIcon && (
            <div className="absolute left-sp160 top-1/2 -translate-y-1/2">
              {leftIcon}
            </div>
          )}
          <input
            type={inputType}
            className={cn(
              inputVariants({ style, size, className }),
              showPlaceholder && "focus:placeholder:opacity-100",
              (finalRightIcon || (type === "password" && showPasswordToggle)) &&
                "pr-10",
              leftIcon && "pl-[40px]",
              readOnly &&
                !plainReadOnly &&
                "bg-semantic-secondary-background-strong-default"
            )}
            readOnly={readOnly || plainReadOnly}
            ref={ref}
            {...props}
          />
          {finalRightIcon && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {finalRightIcon}
            </div>
          )}
        </div>
        {error && <Helper state="error" message={error} />}
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input, inputVariants };
